import {
	Check as CheckIcon,
	Edit as EditIcon,
	Save as SaveIcon,
	ThumbUp as ThumbUpIcon,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { useState } from "react";
import type { Item } from "../types";

interface ItemCardProps {
	item: Item;
	category: "good" | "bad";
	phase: "input" | "merge" | "vote" | "results";
	userId: string;
	authorName?: string;
	authorNames?: string[];
	users?: Record<string, { id: string; name: string }>;
	onVote?: (itemId: string) => void;
	onUpdateItem?: (itemId: string, text: string) => void;
	isDragging?: boolean;
	selectionMode?: boolean;
	isSelected?: boolean;
	onSelect?: (itemId: string) => void;
}

export function ItemCard({
	item,
	category,
	phase,
	userId,
	authorName,
	users,
	onVote,
	onUpdateItem,
	isDragging = false,
	selectionMode = false,
	isSelected = false,
	onSelect,
}: ItemCardProps) {
	// Removed drag and drop functionality
	const [isEditing, setIsEditing] = useState(false);
	const [editText, setEditText] = useState(item.text);

	// Ensure votes is an array (defensive programming)
	const votes = Array.isArray(item.votes)
		? item.votes
		: Array.from(item.votes || []);
	const isVoted = votes.includes(userId);
	const voteCount = votes.length;
	const isAuthor = item.authorId === userId;

	const getCategoryColor = (cat: "good" | "bad") => {
		return cat === "good" ? "success" : "error";
	};

	const getCategoryLabel = (cat: "good" | "bad") => {
		return cat === "good" ? "Went Well" : "Didn't Go Well";
	};

	const handleEdit = () => {
		setIsEditing(true);
		setEditText(item.text);
	};

	const handleSave = () => {
		if (editText.trim() && editText.trim() !== item.text) {
			onUpdateItem?.(item.id, editText.trim());
		}
		setIsEditing(false);
	};

	const handleCancel = () => {
		setEditText(item.text);
		setIsEditing(false);
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			handleSave();
		} else if (event.key === "Escape") {
			handleCancel();
		}
	};

	return (
		<Card
			sx={{
				mb: 1,
				cursor: selectionMode ? "pointer" : "default",
				border: isDragging
					? "2px dashed #1976d2"
					: isSelected
						? "2px solid #1976d2"
						: "1px solid #e0e0e0",
				backgroundColor: isDragging
					? "#f3f9ff"
					: isSelected
						? "#e3f2fd"
						: "background.paper",
			}}
			onClick={selectionMode ? () => onSelect?.(item.id) : undefined}
		>
			<CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
				<Box display="flex" alignItems="flex-start" gap={0.5}>
					{selectionMode && (
						<Checkbox
							checked={isSelected}
							onChange={() => onSelect?.(item.id)}
							size="small"
							sx={{ mt: -0.5 }}
						/>
					)}

					<Box flex={1}>
						<Box
							display="flex"
							alignItems="center"
							justifyContent="space-between"
							mb={0.5}
						>
							<Box display="flex" alignItems="center" gap={1}>
								<Chip
									label={getCategoryLabel(category)}
									color={getCategoryColor(category)}
									size="small"
									variant="outlined"
								/>
								{phase === "results" && (
									<Chip
										label={`${voteCount} vote${voteCount !== 1 ? "s" : ""}`}
										color="primary"
										size="small"
									/>
								)}
							</Box>

							{authorName && (
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontSize: "0.75rem" }}
								>
									by {authorName}
								</Typography>
							)}
						</Box>

						{isEditing ? (
							<Box sx={{ mb: 0.5 }}>
								<TextField
									fullWidth
									multiline
									value={editText}
									onChange={(e) => setEditText(e.target.value)}
									onKeyDown={handleKeyDown}
									autoFocus
									sx={{ mb: 1 }}
								/>
								<Box display="flex" gap={1}>
									<Button
										size="small"
										variant="contained"
										startIcon={<SaveIcon />}
										onClick={handleSave}
										disabled={!editText.trim()}
									>
										Save
									</Button>
									<Button
										size="small"
										variant="outlined"
										onClick={handleCancel}
									>
										Cancel
									</Button>
								</Box>
							</Box>
						) : (
							<Box
								display="flex"
								alignItems="flex-start"
								justifyContent="space-between"
							>
								<Typography variant="body1" sx={{ mb: 0.5, flex: 1 }}>
									{item.text}
								</Typography>
								{isAuthor && phase === "input" && (
									<IconButton
										size="small"
										onClick={handleEdit}
										sx={{ ml: 1, mt: -0.5 }}
										title="Edit item"
									>
										<EditIcon fontSize="small" />
									</IconButton>
								)}
							</Box>
						)}

						{/* Show original texts if this is a merged item */}
						{item.originalTexts && item.originalTexts.length > 1 && (
							<Box
								sx={{ mt: 0.5, p: 0.5, bgcolor: "grey.50", borderRadius: 1 }}
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: "bold" }}
								>
									Original items:
								</Typography>
								{item.originalTexts.map((originalText, index) => (
									<Typography
										key={`${item.id}-original-${index}-${originalText.slice(0, 20)}`}
										variant="caption"
										display="block"
										sx={{
											ml: 1,
											mt: 0.5,
											fontStyle: "italic",
											color: "text.secondary",
										}}
									>
										• {originalText}
									</Typography>
								))}
							</Box>
						)}

						{phase === "vote" && (
							<Button
								variant={isVoted ? "contained" : "outlined"}
								color="primary"
								size="small"
								startIcon={isVoted ? <CheckIcon /> : <ThumbUpIcon />}
								onClick={() => onVote?.(item.id)}
							>
								{isVoted ? "Voted" : "Vote"}
							</Button>
						)}

						{/* Show voter names in results phase */}
						{phase === "results" && voteCount > 0 && users && (
							<Box sx={{ mt: 1, p: 1, bgcolor: "primary.50", borderRadius: 1 }}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: "bold" }}
								>
									Voted by:
								</Typography>
								<Box sx={{ mt: 0.5 }}>
									{votes.map((voterId) => {
										const voter = users?.[voterId as string];
										return voter ? (
											<Chip
												key={voterId as string}
												label={voter.name}
												size="small"
												variant="outlined"
												sx={{ mr: 0.5, mb: 0.5 }}
											/>
										) : null;
									})}
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			</CardContent>
		</Card>
	);
}
